import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface QueryParamsState {
  tab?: string
  status?: string
  type?: string
  search?: string
  pageSize?: string
  cursor?: string
  direction?: string
  firstTime?: string
  isFlagged?: string
  dashboardMode?: string
  hasFunds?: string
}

export function useQueryURLParams(key: string) {
  const [value, setValue] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get(key)
  })

  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search)
      setValue(params.get(key))
    }

    window.addEventListener('popstate', handler)
    window.addEventListener('pushstate', handler) // if you polyfill custom pushstate events
    return () => {
      window.removeEventListener('popstate', handler)
      window.removeEventListener('pushstate', handler)
    }
  }, [key])

  return [value]
}

export function useQueryParams(defaultTab?: string) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read current state directly from URL params - no internal state needed
  const state: QueryParamsState = {
    tab: searchParams.get('tab') || defaultTab,
    status: searchParams.get('status') || undefined,
    type: searchParams.get('type') || undefined,
    search: searchParams.get('search') || undefined,
    pageSize: searchParams.get('pageSize') || undefined,
    cursor: searchParams.get('cursor') || undefined,
    direction: searchParams.get('direction') || undefined,
    firstTime: searchParams.get('firstTime') || undefined,
    isFlagged: searchParams.get('isFlagged') || undefined,
    dashboardMode: searchParams.get('swapMode') || undefined,
    hasFunds: searchParams.get('hasFunds') || undefined,
  }

  // Update URL when state changes
  const updateQueryParams = useCallback(
    (updates: Partial<QueryParamsState>) => {
      const newState = { ...state, ...updates }

      // Update URL params
      const newParams = new URLSearchParams()

      if (newState.tab && newState.tab !== defaultTab) {
        newParams.set('tab', newState.tab)
      }
      if (newState.status) {
        newParams.set('status', newState.status)
      }
      if (newState.type) {
        newParams.set('type', newState.type)
      }
      if (newState.search) {
        newParams.set('search', newState.search)
      }
      if (newState.pageSize) {
        newParams.set('pageSize', newState.pageSize)
      }
      if (newState.cursor) {
        newParams.set('cursor', newState.cursor)
      }
      if (newState.direction) {
        newParams.set('direction', newState.direction)
      }
      if (newState.isFlagged) {
        newParams.set('isFlagged', newState.isFlagged)
      }
      if (newState.dashboardMode) {
        newParams.set('dashboardMode', newState.dashboardMode)
      }
      if (newState.hasFunds) {
        newParams.set('hasFunds', newState.hasFunds)
      }

      setSearchParams(newParams, { replace: true })
    },
    [state, setSearchParams, defaultTab],
  )

  return {
    ...state,
    updateTab: (tab: string) => updateQueryParams({ tab }),
    updateStatus: (status: string | null) => updateQueryParams({ status: status || undefined }),
    updateType: (type: string | null) => updateQueryParams({ type: type || undefined }),
    updateSearch: (search: string) => updateQueryParams({ search: search || undefined }),
    updateDashboardMode: (dashboardMode: string) =>
      updateQueryParams({ dashboardMode: dashboardMode || undefined }),
    updatePagination: (pageSize: string, cursor?: string, direction?: string) =>
      updateQueryParams({
        pageSize,
        cursor: cursor || undefined,
        direction: direction || undefined,
      }),
    updateMultiple: updateQueryParams,
  }
}
